import { z } from 'zod';
import { amountScale, currencyCodeSchema } from './currency.js';
import { baseTotalSchema, currencyBalanceSchema } from './accounts.js';
import { convertAmount, displayScale, exchangeRateSchema, fitsCurrencyScale, moneyAmountSchema, } from './money.js';
import { installmentPlanSchema, installmentPrefillSchema, installmentSchema, } from './installments.js';
import { isoDateSchema, positiveAmountSchema, transactionSchema } from './transactions.js';
/**
 * Debts (Phase 4, docs/plans/phase-4.md). A debt is money a member borrowed (a payable) or lent
 * (a receivable), with an outside person or with another family member. It stays in its own
 * currency for life; what is outstanding is always the sum of its ledger postings. Borrowing,
 * lending, repaying and collecting are never income or expense; only the interest or fee part of
 * a repayment is (the "Interest & fees" category).
 */
export const DEBT_DIRECTIONS = ['borrowed', 'lent'];
export const debtDirectionSchema = z.enum(DEBT_DIRECTIONS);
/** The transaction kinds that make or settle a debt. */
export const DEBT_TRANSACTION_KINDS = [
    'borrow',
    'lend',
    'repay',
    'collect',
    'debt_opening',
];
export const personNameSchema = z.string().trim().min(1).max(80);
const phoneSchema = z.string().trim().min(1).max(40);
const personNoteSchema = z.string().trim().max(500);
const debtNoteSchema = z.string().trim().max(500);
// ---------------------------------------------------------------------------------------------
// People (counterparties)
// ---------------------------------------------------------------------------------------------
export const personSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    phone: z.string().nullable(),
    note: z.string().nullable(),
    archivedAt: z.string().nullable(),
    /** Debts with this person the viewer can see (most used first in lists). */
    debtCount: z.number().int(),
});
export const personListQuerySchema = z.object({
    q: z.string().trim().max(80).optional(),
    includeArchived: z
        .enum(['true', 'false'])
        .default('false')
        .transform((v) => v === 'true'),
    limit: z
        .string()
        .regex(/^[1-9]\d?$|^100$/, 'limit must be 1 to 100')
        .default('50')
        .transform((v) => Number.parseInt(v, 10)),
});
export const personListResponseSchema = z.object({ items: z.array(personSchema) });
export const createPersonRequestSchema = z.object({
    name: personNameSchema,
    phone: phoneSchema.optional(),
    note: personNoteSchema.optional(),
});
/** Rename, phone, note. Null clears the phone or note. */
export const updatePersonRequestSchema = z
    .object({
    name: personNameSchema.optional(),
    phone: phoneSchema.nullable().optional(),
    note: personNoteSchema.nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, 'Nothing to change');
/** Merges this person into another: their debts move to the other person. */
export const mergePersonRequestSchema = z.object({ intoId: z.string().uuid() });
// ---------------------------------------------------------------------------------------------
// Debts
// ---------------------------------------------------------------------------------------------
export const debtPartySchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('outside'), personId: z.string().uuid(), name: z.string() }),
    z.object({ type: z.literal('member'), memberId: z.string().uuid(), name: z.string() }),
]);
export const debtSchema = z.object({
    id: z.string().uuid(),
    /** borrowed: the owner owes the person; lent: the person owes the owner. */
    direction: debtDirectionSchema,
    currency: currencyCodeSchema,
    /** What is still owed, positive (0 when settled). The sum of the debt's postings. */
    outstanding: moneyAmountSchema,
    /** Everything borrowed or lent on this debt, and everything paid back so far. */
    principal: moneyAmountSchema,
    paid: moneyAmountSchema,
    /** `outstanding` in the base currency at the latest family rate (an estimate for others). */
    outstandingInBase: baseTotalSchema,
    /** The member whose debt it is. */
    owner: z.object({ memberId: z.string().uuid(), displayName: z.string() }),
    person: debtPartySchema,
    /** Between two family members: cancels out at family level. */
    internal: z.boolean(),
    /** The other side of a debt between members, when the viewer can see it. */
    pairDebtId: z.string().uuid().nullable(),
    isPrivate: z.boolean(),
    note: z.string().nullable(),
    /** The day of the first transaction (the original date for an existing debt). */
    startedOn: z.string(),
    settled: z.boolean(),
    /** True when the debt has an installment plan (shared by both sides of a debt between members). */
    hasPlan: z.boolean(),
    /** The next installment to pay (the oldest open one), when there is a plan. */
    nextInstallment: installmentSchema.nullable(),
    /** Installments past their due date and not paid or skipped. */
    overdueInstallments: z.number().int(),
    /**
     * A credit item: the unpaid part of an expense (we owe) or income (they owe us). `name` is
     * the transaction's description (or payee); `dueState` is null without a due date or once
     * settled.
     */
    credit: z
        .object({
        transactionId: z.string().uuid(),
        name: z.string(),
        dueOn: z.string().nullable(),
        dueState: z.enum(['upcoming', 'due_today', 'overdue']).nullable(),
    })
        .nullable()
        .default(null),
});
/** Totals per currency, outstanding only, never mixing currencies. */
export const debtTotalsSchema = z.object({
    byCurrency: z.array(currencyBalanceSchema),
    inBase: baseTotalSchema,
});
export const debtListResponseSchema = z.object({
    items: z.array(debtSchema),
    /** Debts with outside people only: family internal debts cancel out and are not counted. */
    totals: z.object({ weOwe: debtTotalsSchema, owedToUs: debtTotalsSchema }),
});
export const debtDetailSchema = debtSchema.extend({
    /** Every transaction on this debt the viewer can see, newest first. */
    history: z.array(transactionSchema),
    plan: installmentPlanSchema.nullable(),
    /** What "Pay installment" (and Pay) start from: the last payment's account and interest. */
    prefill: installmentPrefillSchema,
    /** The viewer may create or change the plan and skip installments. */
    canManagePlan: z.boolean(),
});
export const personDetailSchema = personSchema.extend({
    debts: z.array(debtSchema),
    /** Per currency: outstanding we owe them and they owe us (visible debts only). */
    byCurrency: z.array(z.object({
        currency: currencyCodeSchema,
        weOwe: moneyAmountSchema,
        owedToUs: moneyAmountSchema,
    })),
});
function checkScale(ctx, currency, entries) {
    const scale = amountScale(currency);
    for (const { path, amount } of entries) {
        if (amount === undefined || !moneyAmountSchema.safeParse(amount).success)
            continue;
        if (!fitsCurrencyScale(amount, scale)) {
            ctx.addIssue({
                code: 'custom',
                path,
                message: scale === 0
                    ? `${currency} amounts must be whole numbers`
                    : `${currency} amounts allow at most ${scale} decimals`,
            });
        }
    }
}
/**
 * POST /api/v1/debts (Idempotency-Key required). The other side is an existing person, a new
 * person by name, or another family member. Money moves through `accountId` (and, between
 * members, the other member's `counterpartAccountId`), unless `existing`: a debt from before the
 * family used the app, booked against opening balances without moving any account.
 */
export const createDebtRequestSchema = z
    .object({
    direction: debtDirectionSchema,
    /** Whose debt it is; defaults to the person recording it. */
    ownerMemberId: z.string().uuid().optional(),
    personId: z.string().uuid().optional(),
    personName: personNameSchema.optional(),
    memberId: z.string().uuid().optional(),
    currency: currencyCodeSchema,
    amount: positiveAmountSchema,
    occurredOn: isoDateSchema.optional(),
    note: debtNoteSchema.optional(),
    rate: exchangeRateSchema.optional(),
    existing: z.boolean().default(false),
    accountId: z.string().uuid().optional(),
    counterpartAccountId: z.string().uuid().optional(),
    /** Existing debts only (no money account to follow): mark it private. */
    isPrivate: z.boolean().default(false),
})
    .superRefine((v, ctx) => {
    const given = [v.personId, v.personName, v.memberId].filter((x) => x !== undefined).length;
    if (given !== 1) {
        ctx.addIssue({
            code: 'custom',
            path: ['personId'],
            message: 'Give exactly one of personId, personName or memberId',
        });
    }
    if (v.existing) {
        if (v.accountId !== undefined || v.counterpartAccountId !== undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['accountId'],
                message: 'An existing debt moves no money: leave the accounts out',
            });
        }
    }
    else {
        if (v.accountId === undefined) {
            ctx.addIssue({ code: 'custom', path: ['accountId'], message: 'Pick an account' });
        }
        if (v.memberId !== undefined && v.counterpartAccountId === undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['counterpartAccountId'],
                message: "Pick the other member's account",
            });
        }
        if (v.memberId === undefined && v.counterpartAccountId !== undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['counterpartAccountId'],
                message: 'Only a debt between members has a second account',
            });
        }
        if (v.isPrivate) {
            ctx.addIssue({
                code: 'custom',
                path: ['isPrivate'],
                message: "A debt is private when its account is: it follows the account's privacy",
            });
        }
    }
    checkScale(ctx, v.currency, [{ path: ['amount'], amount: v.amount }]);
});
/**
 * POST /api/v1/debts/:id/payments (Idempotency-Key required): repaying a borrowed debt or
 * collecting a lent one, in the debt's currency. `amount` is the part that reduces the debt and
 * may not exceed what is outstanding. `interest` (repaying an outside person only) is the
 * interest or fee part, booked as an expense on "Interest & fees".
 */
export const debtPaymentRequestSchema = z.object({
    accountId: z.string().uuid(),
    /** Between members: the other member's account the money goes to or comes from. */
    counterpartAccountId: z.string().uuid().optional(),
    amount: positiveAmountSchema,
    interest: positiveAmountSchema.optional(),
    occurredOn: isoDateSchema.optional(),
    note: debtNoteSchema.optional(),
    rate: exchangeRateSchema.optional(),
    /** Marks this installment of the plan as paid ("Pay installment"); it must be open. */
    installment: z.number().int().positive().optional(),
    /**
     * A credit item only: pay in this other currency. `amount` (and `interest`) stay in the
     * debt's currency; what leaves or arrives in the account is converted at the family rate of
     * the payment day (or `rate`), through the FX trading accounts.
     */
    paidCurrency: currencyCodeSchema.optional(),
});
export const debtPaymentResponseSchema = z.object({
    debt: debtSchema,
    transaction: transactionSchema,
});
export const createDebtResponseSchema = debtDetailSchema;
/**
 * What leaves (or arrives in) the account when a credit item in one currency is paid in
 * another: `amount` in the debt's currency converted through the base currency at each one's
 * rate (1 base = rate; "1" for the base currency), rounded to the paid currency's everyday
 * decimals (none for LBP).
 */
export function paidInOtherCurrency(amount, debtRate, paidRate, paidCurrency) {
    return convertAmount(amount, debtRate, paidRate, displayScale(paidCurrency));
}
//# sourceMappingURL=debts.js.map